import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import {
  API_BASE_URL,
  verifyOrCreateUser,
  getUserAgentMergedDataForAgentUpdate,
} from "../../Store/apiStore";
import { getIntegration, APP_ACCENT, APP_LOGO } from "./integrationConfig";
import {
  captureOAuth,
  getOAuth,
  clearOAuth,
  captureHubspotReturn,
} from "../../lib/integrationOAuth";
import decodeToken from "../../lib/decodeToken";
import styles from "./IntegrationThankYou.module.css";

// Shared page for BOTH ends of an integration's OAuth flow — driven by `mode`:
//
//   mode="consent" → Route: /oauth/:platform/consent?oauth_redirect_uri=…&state=…
//     The provider's popup lands here FIRST (before login). We capture
//     redirect_uri + state into localStorage immediately, then Allow →
//     login/onboarding, which eventually returns to the provider and closes its
//     popup. (These said "cookies" for a long time — there are none; the keys
//     survive onboarding because Onboarding.js and SecureRoute restore the
//     oauth_* keys by name after their clear(). Anything NOT on those lists is
//     lost, which is exactly how the HubSpot returnUrl used to go missing.)
//
//   mode="success" (default) → Route: /integrations/:platform/success?ownerEmail=…
//     Shown AFTER the connection succeeded — welcomes the user into the app.
const IntegrationThankYou = ({ mode = "success" }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { platform } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [consentError, setConsentError] = useState("");
  // The connection landed on a different Rexpt account than the one signed in
  // here — show the choice screen instead of picking for the user.
  const [accountMismatch, setAccountMismatch] = useState(false);

  const integration = getIntegration(platform);
  const ownerEmail = searchParams.get("ownerEmail");
  const ownerName = searchParams.get("ownerName");
  const fullOtp = "notRequired";
  const isConsent = mode === "consent";

  // The callback now redirects here on FAILURE too (connected=0&error=…) rather
  // than returning a raw 500 body — otherwise the user ends up connected on the
  // provider's side but stranded outside the app. Render the failure instead of
  // claiming success.
  const connectFailed =
    searchParams.get("connected") === "0" || !!searchParams.get("error");
  const connectError = searchParams.get("error") || "";
  const connectErrorDetail = searchParams.get("error_description") || "";
  const userDenied = connectError === "access_denied";

  // Re-run the connect from the top. Only HubSpot and Clover expose an
  // app-initiated start at /integration/auth/<platform>; Zapier's entry is nested
  // (/integration/zapier/auth/zapier) and Make has none at all — Make always
  // initiates from its own side. So anything else goes to the Integrations page,
  // where each platform's own button starts the correct flow.
  const AUTH_START_PATH = {
    hubspot: "/integration/auth/hubspot",
    clover: "/integration/auth/clover",
  };

  const retryConnect = () => {
    const startPath = AUTH_START_PATH[String(platform || "").toLowerCase()];
    if (!startPath) {
      navigate("/integrations");
      return;
    }
    // Pass the logged-in userId as `state` when we have one, so the retry links
    // to THIS account instead of resolving by the provider's email.
    const t = localStorage.getItem("token");
    const uid = t ? String(decodeToken(t)?.id || "") : "";
    window.location.href = `${API_BASE_URL}${startPath}${
      uid ? `?state=${encodeURIComponent(uid)}` : ""
    }`;
  };

  // Email of the rexpt account the user is currently logged in as (if any).
  // Shown on the consent screen so they can confirm or switch accounts.
  const loggedInToken = localStorage.getItem("token");
  const loggedInEmail =
    localStorage.getItem("userEmail") ||
    (loggedInToken ? decodeToken(loggedInToken)?.email : "") ||
    "";

  // The Rexpt account this connection ended up on, but ONLY when it differs from
  // the provider's email — that is the case where showing one email alone is
  // misleading. Same email (or signed out, where there is nothing to compare
  // against) keeps the original single-card layout. Declared here, AFTER
  // loggedInEmail: reading it any earlier is a temporal-dead-zone crash.
  const linkedToRexptEmail =
    loggedInEmail &&
    ownerEmail &&
    loggedInEmail.trim().toLowerCase() !== ownerEmail.trim().toLowerCase()
      ? loggedInEmail
      : "";

  // App ka apna brand color -> poora page isi theme me rehta hai.
  const themeStyle = { "--accent": APP_ACCENT };

  // ── Consent mode: capture redirect_uri + state on first paint ──────────
  // MUST run before anything clears storage — the oauth_* keys are restored by
  // name after onboarding's clear(), but only if they were written first.
  useEffect(() => {
    if (!isConsent) return;
    const ok = captureOAuth(window.location.search, platform);
    if (!ok) {
      setConsentError(`Missing redirect_uri from ${integration.label}.`);
      return;
    }
    const { redirectUri, state } = getOAuth();

    // ── Zapier: hand off to zapier.com's Workflow-API OAuth ──────────────────
    // This reads the email + returns the access/refresh token. We identify the
    // user from the BROWSER login and route accordingly:
    //   • logged-in  → pass userId  → Flow 2: connection links to THIS Rexpt
    //                                  account (zapier.com email is ignored).
    //   • not logged → no userId    → Flow 1: backend auto-creates the user from
    //                                  the zapier.com email, then onboarding.
    if (platform === "zapier") {
      const token = localStorage.getItem("token");
      const url = new URL(
        `${API_BASE_URL}/integration/zapier/auth/zapier/provider`,
      );
      if (redirectUri) url.searchParams.set("redirect_uri", redirectUri);
      if (state) url.searchParams.set("state", state); // Zapier's own CSRF state
      // Send the SIGNED login JWT (not the raw userId, which would be spoofable).
      // The backend jwt.verify()s it → proven user → Flow 2 (link to THIS account).
      if (token) url.searchParams.set("rexpt_user", token);
      console.log(
        "[oauth] zapier consent → provider OAuth. logged-in:",
        !!token,
      );
      window.location.replace(url.toString());
      return;
    }

    console.log(
      "[oauth] consent captured. platform:",
      platform,
      "state:",
      state || "MISSING",
    );
  }, [isConsent, platform, integration.label]);

  const handleAllow = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      // Logged in → always go to /fast-agent-detail; it closes the OAuth loop on load.
      navigate("/fast-agent-detail");
      return;
    }
    // Not logged in → signup/onboarding; OAuth params persist for the later return.
    navigate("/");
  };

  // "Use a different account" — clear the current rexpt session and send the
  // user to a fresh login, KEEPING the captured oauth_redirect_uri + state in
  // localStorage so the Make handshake still completes after they log in again.
  const handleSwitchAccount = () => {
    // Remove ONLY the user-session keys — leave oauth_redirect_uri / oauth_state
    // / oauth_platform intact so returnToProvider() can still close the loop.
    localStorage.removeItem("token");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("onboardComplete");
    localStorage.removeItem("paymentDone");
    // Back to login/signup. The OAuth keys persist in localStorage, so once the
    // user logs in with the new account and has an agent, returnToProvider()
    // closes the OAuth loop with the new account's token.
    navigate("/");
  };

  // Deny = user cancelled. We deliberately DO NOT redirect to Make.com's
  // callback with ?error=access_denied — Make has no graceful cancel UI and
  // always renders a raw JSON error page (InvalidConfigurationError / SC424)
  // inside the popup. Instead we just close the popup: it disappears and Make's
  // parent dialog ("Waiting for authorization…") simply stays put, which is the
  // cleaner outcome. Make may log a harmless "[401] Invalid token" for this
  // abandoned attempt — that does NOT affect real connections (Allow), it's
  // only the noise from a popup that closed without finishing the handshake.
  const handleDeny = () => {
    clearOAuth();
    window.close();
    // Fallback if the browser blocks window.close() (e.g. the consent URL was
    // opened directly, not as a script-opened popup) → show a cancel message.
    setConsentError("Connection cancelled. You can close this window.");
  };

  const handleProceed = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);

      // Clover (aur koi bhi flow jo backend se JWT bhejta hai): token URL mein
      // aaya → seedha store karke login + dashboard. Signup screen avoid.
      const tokenParam = searchParams.get("token");
      if (tokenParam) {
        localStorage.setItem("token", tokenParam);
        if (ownerEmail) localStorage.setItem("userEmail", ownerEmail);
        // Naya user (isNew=1) → Personal Details page (HubSpot jaisa). Existing
        // user → seedha dashboard (onboardComplete set karke).
        if (searchParams.get("isNew") === "1") {
          navigate(`/details?name=${encodeURIComponent(ownerName || "")}`);
        } else {
          localStorage.setItem("onboardComplete", "true");
          navigate("/fast-agent-detail");
        }
        return;
      }

      // Already logged in → take them straight into their own account — SAME
      // handoff the shared success screen uses for Zapier/Make.
      const token = localStorage.getItem("token");
      if (token) {
        const onboardComplete =
          localStorage.getItem("onboardComplete") === "true";
        navigate(onboardComplete ? "/fast-agent-detail" : "/details");
        return;
      }

      // Not logged in (e.g. HubSpot-initiated install) → resolve/create the
      // rexpt account from the HubSpot owner email, then continue onboarding.
      if (!ownerEmail) {
        console.warn("Missing required info to verify.");
        setSubmitting(false);
        return;
      }

      const data = await verifyOrCreateUser(ownerEmail, fullOtp);

      if (data?.res1) {
        const verifyStatus = data?.res1?.data?.verifiedStatus === true;

        if (verifyStatus) {
          localStorage.setItem("userEmail", ownerEmail);
          localStorage.setItem("token", data.res?.data?.token || "");
          // profile kept for parity with previous page (not currently persisted)
          void `${API_BASE_URL?.split("/api")[0]}${
            (data?.res?.data?.profile || "").split("public")[1] || ""
          }`;
        }
      }

      // Navigate even without a token: SecureRoute then sends the user to login
      // and PRESERVES the oauth_* keys, so the pending loop still completes after
      // they sign in. That holds for HubSpot too now that its returnUrl lives on
      // those same keys — which is why this needs no special-casing.
      navigate(`/details?name=${encodeURIComponent(ownerName || "")}`);
    } catch (error) {
      console.error("Error during login or redirect:", error);
      navigate("/error?message=login_failed");
    }
  };

  useEffect(() => {
    if (isConsent) return;
    // HubSpot secure install: stash the returnUrl NOW, onto the SAME oauth_*
    // localStorage keys Zapier/Make use (see captureHubspotReturn) so it survives
    // onboarding's clear(). Fired later, once an agent exists, to close the
    // install popup + tick "Complete" on HubSpot.
    captureHubspotReturn(window.location.search);
    if (!ownerEmail || !ownerName) {
      console.warn("Missing params:", { platform, ownerEmail, ownerName });
    }
  }, [isConsent, platform, ownerEmail, ownerName]);

  // HubSpot: different-account correction (FRONTEND-only). On a marketplace
  // install the backend resolves the account from the HubSpot owner email — it
  // has no way to know the rexpt account logged into THIS browser. So if the user
  // is logged in as one account (Y) but the connection was attached to a DIFFERENT
  // account (X, from the HubSpot email), re-attach it to the LOGGED-IN account by
  // re-running the connect with state = Y. The backend then links it to Y
  // (state-based) — same as every other platform links to the proven logged-in
  // account. We compare userIds (NOT emails): the HubSpot owner email may
  // legitimately differ from the rexpt email on an app-side connect (already on
  // the right account) → that stays a no-op. Not-logged-in install has no token →
  // no-op. So this fires ONLY on the real cross-account case.
  useEffect(() => {
    if (isConsent) return;
    if (String(platform).toLowerCase() !== "hubspot") return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const connectedUserId = searchParams.get("userId"); // account the backend attached to
    const loggedInUserId = String(decodeToken(token)?.id || "");
    if (
      connectedUserId &&
      loggedInUserId &&
      String(connectedUserId) !== loggedInUserId
    ) {
      // ASK, don't silently re-link. This used to fire the re-connect on its own,
      // which moved the connection to another account without the user ever
      // seeing it happen — and the success card still showed the HubSpot email,
      // so there was no way to tell which Rexpt account ended up connected.
      // Mirrors the Make/Zapier consent screen, which names the account it is
      // about to connect and offers "Use a different account".
      console.log(
        "[hubspot] connection is on a different account → asking the user",
      );
      setAccountMismatch(true);
    }
  }, [isConsent, platform, searchParams]);

  // "Link to <signed-in account>" — re-run the connect with state=<userId>. The
  // backend then resolves the account by userId instead of by the HubSpot owner
  // email, moving the connection onto the account the user is actually using.
  const handleLinkToLoggedIn = async () => {
    if (submitting) return;
    const token = localStorage.getItem("token");
    const relinkToken = searchParams.get("relinkToken");
    if (!token) return;
    try {
      setSubmitting(true);
      // Move the row server-side. The old approach re-ran the whole OAuth flow
      // with ?state=<userId>, which bounced the user through HubSpot again even
      // though they had just authorised — we already hold the token, so only the
      // row needs to move. relinkToken proves this browser completed THAT
      // install; without it the endpoint refuses.
      if (!relinkToken) throw new Error("relink_token_missing");
      const res = await fetch(`${API_BASE_URL}/integration/hubspot/relink`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ relinkToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `relink_failed_${res.status}`);
      }
      setAccountMismatch(false); // now on the right account → normal success card
      setSubmitting(false);
    } catch (err) {
      console.error("[hubspot] relink failed:", err.message);
      setSubmitting(false);
      // Fall back to the old re-connect path so the user is never stuck.
      const uid = String(decodeToken(token)?.id || "");
      if (uid) {
        // [FIX: origin-redirect] send the origin so the OAuth callback lands
        // back on THIS frontend (.in / .ai) instead of APP_NAVIGATION_URL.
        window.location.href = `${API_BASE_URL}/integration/auth/${platform}?state=${encodeURIComponent(
          uid,
        )}&origin=${encodeURIComponent(window.location.origin)}`;
      }
    }
  };

  // "Use <HubSpot email> instead" — leave the connection where the backend put
  // it (the HubSpot owner's account) and switch this browser over to that
  // account, onboarding it like any other new user. Drops ONLY the session keys:
  // the oauth_* keys must survive so a pending install can still be finalised.
  const handleUseProviderAccount = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("onboardComplete");
    localStorage.removeItem("paymentDone");
    setAccountMismatch(false);
    // No token now → handleProceed takes the verifyOrCreateUser(ownerEmail)
    // branch, which signs the user in as that account and continues onboarding.
    handleProceed();
  };

  // Clover onboarding flag — user Clover se redirect ho kar is success page pe
  // aaya (platform=clover). Ise sessionStorage me rakho; baad me createAgent is
  // flag ke hone pe HI Clover linking karega — taaki har (non-Clover) user ke
  // agent creation pe DB lookup na ho. Same-tab onboarding (details → business
  // step → createAgent) tak sessionStorage bana rehta hai.
  useEffect(() => {
    if (isConsent) return;
    if (String(platform).toLowerCase() === "clover") {
      sessionStorage.setItem("fromClover", "1");
      const mId = searchParams.get("merchantId");
      if (mId) sessionStorage.setItem("cloverMerchantId", mId);
    }
  }, [isConsent, platform, searchParams]);

  // Clover "Open the app" re-launch: backend already-connected user ke liye JWT
  // bhejta hai (?token=…&already=1). Token store karke seedha dashboard le jao —
  // dobara connect/onboarding screen nahi dikhana.
  useEffect(() => {
    if (isConsent) return;
    const tokenParam = searchParams.get("token");
    const already = searchParams.get("already");
    if (tokenParam && already === "1") {
      localStorage.setItem("token", tokenParam);
      if (ownerEmail) localStorage.setItem("userEmail", ownerEmail);
      // onboardComplete set karo — warna SecureRoute /fast-agent-detail se
      // /details pe bhej deta hai (line 63-68). Clover user already onboarded.
      localStorage.setItem("onboardComplete", "true");
      // Backend ne merchant_id → user → agent resolve kiya:
      //   • multiAgent=1 (2+ agents) → seedha /dashboard (user agent chunega)
      //   • agentId (1 agent)        → SelectAgentId set → /fast-agent-detail
      (async () => {
        // 2+ agents → dashboard
        if (searchParams.get("multiAgent") === "1") {
          navigate("/dashboard");
          return;
        }
        const agentId = searchParams.get("agentId");
        const businessId = searchParams.get("businessId");
        if (agentId) {
          sessionStorage.setItem("SelectAgentId", agentId);
          if (businessId)
            sessionStorage.setItem("SelectAgentBusinessId", businessId);
          // Merged agent data → sessionStorage (agentId + businessId se)
          try {
            const merged = await getUserAgentMergedDataForAgentUpdate(
              agentId,
              businessId,
            );
            const ag = merged?.data?.agent;
            if (ag) {
              if (ag.agentName)
                sessionStorage.setItem("agentName", ag.agentName);
              if (ag.agentRole)
                sessionStorage.setItem("agentRole", ag.agentRole);
              if (ag.avatar) sessionStorage.setItem("avatar", ag.avatar);
              const g = ag.agentGender || ag.gender;
              if (g) sessionStorage.setItem("agentGender", g);
              if (ag.knowledgeBaseId)
                sessionStorage.setItem("knowledgeBaseId", ag.knowledgeBaseId);
            }
          } catch (e) {
            console.error("[clover] merged agent data fetch failed:", e);
          }
        }
        navigate("/fast-agent-detail"); // home/dashboard page
      })();
    }
  }, [isConsent, searchParams, ownerEmail, navigate]);

  // Shared brand row (Rexpt ↔ platform) used by both modes.
  const brandRow = (
    <div className={styles.brandRow}>
      <div className={styles.brandLogo}>
        <img src={APP_LOGO} alt="RexPT" className={styles.logoImg} />
      </div>
      <div className={styles.connector}>
        <span className={styles.dot} />
        <span className={styles.dot} />
        <span className={styles.dot} />
      </div>
      <div className={styles.brandLogo}>
        {integration.logo ? (
          <img
            src={integration.logo}
            alt={integration.label}
            className={`${styles.logoImg} ${styles.iconLogo}`}
          />
        ) : (
          <span className={styles.platformInitial}>{integration.initial}</span>
        )}
      </div>
    </div>
  );

  // ── Consent screen (start of the flow) ────────────────────────────────
  if (isConsent) {
    return (
      <div className={styles.page} style={themeStyle}>
        <div className={styles.card}>
          {brandRow}

          <h1 className={styles.title}>Connect Rexpt to {integration.label}</h1>
          <p className={styles.subtitle}>
            {integration.label} will be able to read your agents and receive
            your call &amp; booking events so it can trigger your scenarios.
          </p>

          {consentError && (
            <p style={{ color: "#c0392b", fontSize: 13, marginTop: 12 }}>
              {consentError}
            </p>
          )}

          {/* Show which rexpt account will be connected + let the user switch. */}
          {loggedInToken && (
            <div className={styles.accountCard} style={{ marginTop: 16 }}>
              <div className={styles.avatar}>
                {(loggedInEmail || "?").charAt(0).toUpperCase()}
              </div>
              <div className={styles.accountInfo}>
                <span className={styles.accountName}>Connecting as</span>
                {loggedInEmail && (
                  <span className={styles.accountEmail}>{loggedInEmail}</span>
                )}
              </div>
            </div>
          )}

          {loggedInToken && (
            <button
              type="button"
              onClick={handleSwitchAccount}
              className={styles.linkButton}
              style={{
                background: "none",
                border: "none",
                color: "var(--accent)",
                cursor: "pointer",
                fontSize: 13,
                marginTop: 10,
                textDecoration: "underline",
              }}
            >
              Use a different account
            </button>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <button onClick={handleDeny} className={styles.modalSecondary}>
              Deny
            </button>
            <button onClick={handleAllow} className={styles.proceedButton}>
              Allow
            </button>
          </div>

          <p className={styles.footnote}>
            You can disconnect anytime from your dashboard.
          </p>
        </div>
      </div>
    );
  }

  // ── Already-connected re-launch ("Open the app") ─────────────────────
  // Backend ne ?already=1&token= bheja → upar wala useEffect token store karke
  // dashboard pe le ja raha hai. Success UI mat dikhao (flash avoid) — bas
  // ek chhota "opening…" jab tak redirect ho.
  if (searchParams.get("already") === "1" && searchParams.get("token")) {
    return null; // kuch mat dikhao — useEffect turant dashboard pe le jata hai
  }

  // ── Account-choice screen ─────────────────────────────────────────────
  // The provider connected an account whose email differs from the Rexpt account
  // signed in here. Name BOTH and let the user decide — showing only one email
  // (as the plain success card does) is exactly what made this confusing.
  if (accountMismatch) {
    return (
      <div className={styles.page} style={themeStyle}>
        <div className={styles.card}>
          {brandRow}

          <span className={styles.badge}>Connected</span>

          <h1 className={styles.title}>Which account should we link?</h1>
          <p className={styles.subtitle}>
            Your {integration.label} account is connected, but it belongs to a
            different email than the one you&apos;re signed in with.
          </p>

          <div className={styles.accountCard}>
            <div className={styles.avatar}>
              {(ownerEmail || "?").charAt(0).toUpperCase()}
            </div>
            <div className={styles.accountInfo}>
              <span className={styles.accountName}>
                {integration.label} account
              </span>
              <span className={styles.accountEmail}>{ownerEmail}</span>
            </div>
          </div>

          <div className={styles.accountCard} style={{ marginTop: 10 }}>
            <div className={styles.avatar}>
              {(loggedInEmail || "?").charAt(0).toUpperCase()}
            </div>
            <div className={styles.accountInfo}>
              <span className={styles.accountName}>Signed in to Rexpt as</span>
              <span className={styles.accountEmail}>{loggedInEmail}</span>
            </div>
          </div>

          <button
            onClick={handleLinkToLoggedIn}
            className={styles.proceedButton}
            style={{ marginBottom: 10 }}
          >
            Use this same  Rexpt account
          </button>

          <button
            type="button"
            onClick={handleUseProviderAccount}
            className={styles.linkButton}
            style={{
              background: "none",
              border: "none",
              color: "var(--accent)",
              cursor: "pointer",
              fontSize: 13,
              marginTop: 12,
              textDecoration: "underline",
            }}
          >
            Use a different account ({ownerEmail})
          </button>

          <p className={styles.footnote}>
            You can change this anytime from your dashboard.
          </p>
        </div>
      </div>
    );
  }

  // ── Failure screen ────────────────────────────────────────────────────
  // Reached when the callback couldn't finish (connected=0). The provider may
  // ALREADY show the app as connected — it marks the install the moment the user
  // grants access — so say that plainly rather than implying nothing happened.
  if (connectFailed) {
    return (
      <div className={styles.page} style={themeStyle}>
        <div className={styles.card}>
          {brandRow}

          <h1 className={styles.title}>
            {userDenied
              ? `${integration.label} connection cancelled`
              : `Couldn't finish connecting ${integration.label}`}
          </h1>
          <p className={styles.subtitle}>
            {userDenied
              ? "You cancelled before granting access. You can try again whenever you're ready."
              : `Your ${integration.label} account may already show Rexpt as connected, but we couldn't complete the setup on our side. Please try again.`}
          </p>

          {connectErrorDetail && (
            <p
              style={{
                color: "#c0392b",
                fontSize: 12,
                marginTop: 12,
                wordBreak: "break-word",
              }}
            >
              {connectErrorDetail}
            </p>
          )}

          <button onClick={retryConnect} className={styles.proceedButton}>
            Try again
          </button>

          <p className={styles.footnote}>
            Still stuck? Reach out and we'll get it sorted.
          </p>
        </div>
      </div>
    );
  }

  // ── Success screen (end of the flow) ──────────────────────────────────
  return (
    <div className={styles.page} style={themeStyle}>
      <div className={styles.card}>
        {/* Brand row: RexPT ↔ platform connected */}
        {brandRow}

        {/* Animated success check */}
        <div className={styles.checkWrap}>
          <svg viewBox="0 0 52 52" className={styles.checkSvg}>
            <circle
              className={styles.checkCircle}
              cx="26"
              cy="26"
              r="24"
              fill="none"
            />
            <path
              className={styles.checkMark}
              fill="none"
              d="M14 27 L22 35 L38 18"
            />
          </svg>
        </div>

        <span className={styles.badge}>Connected</span>

        <h1 className={styles.title}>
          {integration.label} connected successfully
        </h1>
        <p className={styles.subtitle}>
          Your {integration.label} account is now linked to your AI agent.
        </p>

        {/* Connected account summary.
            `ownerEmail` is the PROVIDER's email, never the Rexpt one — the two
            are often different, and showing only the provider's made it look
            like the connection had landed on the wrong Rexpt account (it
            hadn't). So when they differ, name both and label which is which. */}
        {(ownerName || ownerEmail) && (
          <div className={styles.accountCard}>
            <div className={styles.avatar}>
              {(ownerName || ownerEmail || "?").charAt(0).toUpperCase()}
            </div>
            <div className={styles.accountInfo}>
              <span className={styles.accountName}>
                {linkedToRexptEmail
                  ? `${integration.label} account`
                  : ownerName || `${integration.label} account`}
              </span>
              {ownerEmail && (
                <span className={styles.accountEmail}>{ownerEmail}</span>
              )}
            </div>
          </div>
        )}

        {linkedToRexptEmail && (
          <div className={styles.accountCard} style={{ marginTop: 10 }}>
            <div className={styles.avatar}>
              {linkedToRexptEmail.charAt(0).toUpperCase()}
            </div>
            <div className={styles.accountInfo}>
              <span className={styles.accountName}>Linked to Rexpt account</span>
              <span className={styles.accountEmail}>{linkedToRexptEmail}</span>
            </div>
          </div>
        )}

        <button
          onClick={handleProceed}
          className={styles.proceedButton}
          disabled={submitting}
        >
          {submitting ? "Please wait…" : "Get Started"}
        </button>

        <p className={styles.footnote}>
          You can manage this connection anytime from your dashboard.
        </p>
      </div>
    </div>
  );
};

export default IntegrationThankYou;
